/**
 * HTTP client. Same-origin, cookie-based auth (the backend sets httpOnly
 * session cookies), so no token handling here. Normalizes errors to
 * { status, message }.
 *
 * Access tokens are deliberately short-lived, so a 401 usually means "expired",
 * not "logged out". This client treats it that way: it silently exchanges the
 * refresh cookie for a new access token and replays the request once. The user
 * sees nothing. Only when the refresh itself fails is the session really over,
 * and the 401 is passed through for the caller to handle.
 */

// Retrying these would be circular or meaningless: a failed refresh must not
// trigger another refresh, and login/logout 401s are answers, not staleness.
const PATHS_EXCLUDED_FROM_RETRY = ["/api/auth/refresh", "/api/auth/login", "/api/auth/logout"];

class ApiService {
  constructor() {
    // Shared by every caller that hits a 401 at once, so a page issuing six
    // parallel requests performs ONE refresh rather than six competing ones —
    // which would rotate the token six times and trip the reuse detection.
    this.inFlightRefresh = null;
  }

  isRetryable(path) {
    return !PATHS_EXCLUDED_FROM_RETRY.some((excluded) => path.startsWith(excluded));
  }

  /** Renew the access token. Resolves true when the session survived. */
  renewSession() {
    if (!this.inFlightRefresh) {
      this.inFlightRefresh = fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "same-origin",
      })
        .then((res) => res.ok)
        .catch(() => false)
        .finally(() => {
          this.inFlightRefresh = null;
        });
    }
    return this.inFlightRefresh;
  }

  async sendRequest(path, method, body, isForm) {
    const options = { method, credentials: "same-origin", headers: {} };

    if (isForm) {
      options.body = body; // FormData sets its own Content-Type
    } else if (body != null) {
      options.headers["Content-Type"] = "application/json";
      options.body = JSON.stringify(body);
    }

    const res = await fetch(path, options);
    const isJson = (res.headers.get("content-type") || "").includes("application/json");
    const payload = isJson ? await res.json().catch(() => null) : null;
    return { res, payload };
  }

  async call(path, method = "GET", body = null, { isForm = false, allowRetry = true } = {}) {
    let { res, payload } = await this.sendRequest(path, method, body, isForm);

    if (res.status === 401 && allowRetry && this.isRetryable(path)) {
      const renewed = await this.renewSession();
      // allowRetry: false on the replay, so a still-401 response ends here
      // rather than looping.
      if (renewed) return this.call(path, method, body, { isForm, allowRetry: false });
    }

    if (!res.ok) {
      throw { status: res.status, message: payload?.error || `Request failed (${res.status})` };
    }
    return payload;
  }

  /** POST FormData via XHR instead of fetch, so upload progress events are available. */
  postFormWithProgress(path, formData, onProgress, { allowRetry = true } = {}) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", path);
      xhr.withCredentials = true;
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress?.(e.loaded, e.total);
      };
      xhr.onload = async () => {
        const isJson = (xhr.getResponseHeader("content-type") || "").includes("application/json");
        let payload = null;
        if (isJson) {
          try {
            payload = JSON.parse(xhr.responseText);
          } catch {
            payload = null;
          }
        }

        if (xhr.status >= 200 && xhr.status < 300) return resolve(payload);

        // An upload batch can outlive a short access token. The FormData is
        // still in memory, so renew and re-send this batch once rather than
        // failing a long upload the user already waited through.
        if (xhr.status === 401 && allowRetry && (await this.renewSession())) {
          return this.postFormWithProgress(path, formData, onProgress, { allowRetry: false })
            .then(resolve)
            .catch(reject);
        }

        reject({ status: xhr.status, message: payload?.error || `Request failed (${xhr.status})` });
      };
      xhr.onerror = () => reject({ status: 0, message: "Network error" });
      xhr.send(formData);
    });
  }

  get(path) {
    return this.call(path, "GET");
  }
  post(path, body, opts) {
    return this.call(path, "POST", body, opts);
  }
  put(path, body, opts) {
    return this.call(path, "PUT", body, opts);
  }
  del(path) {
    return this.call(path, "DELETE");
  }
}

export default new ApiService();
